import React, { useMemo } from 'react';

import { OfferType } from 'constants/enums';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import { useOfferTable } from '../../hooks/useOfferTable';
import { OfferPageLayout } from '../../components/OfferPageLayout';

import { usePromotionsColumns } from './hooks/usePromotionsColumns';

const PromotionsTable = (): React.ReactElement => {
  const {
    getOffers,
    pricingsResult,
    isPricingsLoading,
    currentPage,
    setCurrentPage,
    sorting,
    updateSorting,
    externalIdFilter,
    setExternalIdFilter,
    nameFilter,
    setNameFilter,
    permissions,
    canManageOffers,
    canUserEdit,
    actions,
    selectedOffer,
    isDeleteDialogOpen,
    showLimitedEditModal,
    handleDeleteOffer,
    handleCreate,
    handleCloseLimitedEditModal,
    handleCloseDeleteDialog,
    rowsPerPage,
    offers,
    processOffers
  } = useOfferTable({
    offerType: OfferType.SPECIAL_OFFER,
    offerLabel: 'Promotion',
    needsPricings: true
  });

  const { columns } = usePromotionsColumns({
    sorting,
    updateSorting,
    isLoading: getOffers.isLoading,
    actions,
    permissions,
    isPricingsLoading,
    pricingsResult
  });

  const processed = useMemo(
    () => processOffers(offers),
    [offers, processOffers]
  );

  return (
    <OfferPageLayout
      title="Promotions"
      buttonText={processed.buttonText}
      onCreateClick={handleCreate}
      canUserEdit={canUserEdit}
      canManageOffers={canManageOffers}
      offerLabel="Promotions"
      showFilters={offers.length > 0}
      externalIdFilter={externalIdFilter}
      onExternalIdFilterChange={setExternalIdFilter}
      nameFilter={nameFilter}
      onNameFilterChange={setNameFilter}
      offers={offers}
      displayData={processed.sorted}
      columns={columns}
      totalCount={processed.totalCount}
      totalPages={processed.totalPages}
      rowsPerPage={rowsPerPage}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      isLoading={getOffers.isLoading}
      emptyStateTitle="Add your first Promotion"
      emptyStateDescription="Promotions convert better - create your first one to boost sales and engagement"
      createButtonPrefix="Promotion"
      bodyDataTestId="dashPromotionsList"
      offerType={OfferType.SPECIAL_OFFER}
      offerDesignType={OfferDesignTypes.SpecialOffer}
      selectedOffer={selectedOffer}
      showLimitedEditModal={showLimitedEditModal}
      onCloseLimitedEditModal={handleCloseLimitedEditModal}
      isDeleteDialogOpen={isDeleteDialogOpen}
      onCloseDeleteDialog={handleCloseDeleteDialog}
      onConfirmDelete={handleDeleteOffer}
      deleteModalTitle="Delete Promotion"
      deleteModalDescription="Are you sure you want to delete this promotion?"
    />
  );
};

export default PromotionsTable;
